import React, { useContext } from 'react';
import { UserContext } from '@/context/UserContext';
import useI18n from '@/hooks/useI18n';

const ImpersonationBadge: React.FC = () => {
  const { t } = useI18n();
  const userContext = useContext(UserContext);

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }

  const { isImpersonating, impersonateBrandName, exitImpersonation } =
    userContext;

  if (!isImpersonating || !impersonateBrandName) {
    return null;
  }

  return (
    <>
      {/* Overlay subtil pour attirer l'attention */}
      <div className="fixed inset-0 bg-gradient-to-b from-red-50/30 via-transparent to-transparent pointer-events-none z-40" />

      {/* Badge principal - Bandeau fixe en haut */}
      <div className="fixed top-0 left-0 right-0 z-50 animate-in slide-in-from-top duration-300">
        <div className="bg-gradient-to-r from-red-500 via-pink-500 to-red-600 shadow-lg border-b border-red-400/20 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-12">
              {/* Contenu principal */}
              <div className="flex items-center space-x-3 flex-1">
                {/* Icône avec animation de pulse */}
                <div className="relative">
                  <div className="absolute inset-0 bg-white/20 rounded-full animate-pulse" />
                  <div className="relative bg-white/10 backdrop-blur-sm rounded-full p-1.5">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-white"
                    >
                      <path
                        d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
                        fill="currentColor"
                        opacity="0.8"
                      />
                      <path
                        d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z"
                        fill="currentColor"
                        opacity="0.6"
                      />
                      {/* Masque d'impersonation */}
                      <circle
                        cx="16"
                        cy="8"
                        r="4"
                        fill="currentColor"
                        className="opacity-90"
                      />
                      <path
                        d="M14 8C14 6.89543 14.8954 6 16 6"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        className="opacity-80"
                      />
                    </svg>
                  </div>
                </div>

                {/* Texte avec design typographique élégant */}
                <div className="flex items-center space-x-2">
                  <span className="text-white/90 text-sm font-medium tracking-wide">
                    {t('admin.impersonate.viewingAs')}
                  </span>
                  <span className="text-white font-semibold text-sm tracking-wide px-2 py-0.5 bg-white/10 rounded-full backdrop-blur-sm">
                    {impersonateBrandName}
                  </span>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <div className="hidden sm:block text-white/70 text-xs font-medium"></div>
                <button
                  onClick={exitImpersonation}
                  className="group relative overflow-hidden bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-1.5 transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  <span className="relative text-white text-sm font-medium tracking-wide">
                    {t('admin.impersonate.backToAdmin')}
                  </span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline-block ml-2 text-white/80 group-hover:text-white transition-colors duration-200"
                  >
                    <path
                      d="M9 21L17 12L9 3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Espace réservé pour éviter que le contenu ne soit caché */}
      <div className="h-12" />
    </>
  );
};

export default ImpersonationBadge;
